import { Invitation } from '@playcastdotio/Messaging';

interface InvitationSnapshot {
  invitation: Invitation;
  reason: 'added' | 'updated' | 'removed';
  timestamp: number;
  sequenceId: number; // For ordering snapshots
}

interface InvitationTimeline {
  invitationId: string;
  snapshots: InvitationSnapshot[];
  lastUpdated: number;
  firstSeen: number;
}

interface LRUCache {
  timelines: Record<string, InvitationTimeline>; // Keyed by invitation ID
  order: string[]; // Most recently used first (invitation IDs)
  lastModified: number; // Timestamp for conflict resolution
  version: number; // Version number for optimistic locking
}

// Legacy interface for backwards compatibility
interface CachedInvitation {
  invitation: Invitation;
  lastSeen: number;
  reason: 'added' | 'updated' | 'removed';
  timestamp: number;
}

const CACHE_KEY = 'invitationDiagnosticsCache';
const CACHE_ENABLED_KEY = 'invitationDiagnosticsCacheEnabled';
const MAX_CACHE_SIZE = 200; // Configurable max size

// Global enable/disable preference (default: disabled for performance)
let cacheEnabled = localStorage.getItem(CACHE_ENABLED_KEY) === 'true' || false;

/**
 * LRU Cache for storing invitation diagnostics in localStorage
 * Keeps track of removed/updated invitations for debugging purposes
 */
class InvitationDiagnosticsCache {
  private cache: LRUCache;
  private seenInvitationIds: Set<string> = new Set(); // Track which invitation IDs we've seen before

  constructor() {
    this.cache = this.loadFromStorage();
    // Initialize seen IDs from existing cache
    this.seenInvitationIds = new Set(Object.keys(this.cache.timelines));

    // Listen for storage changes from other tabs
    window.addEventListener('storage', (event) => {
      if (event.key === CACHE_KEY && event.newValue) {
        try {
          const newCache = JSON.parse(event.newValue) as LRUCache;
          // Only update if the other tab has a newer version
          if (newCache.version > this.cache.version) {
            console.log('Cache updated in another tab (newer version), reloading...', {
              oldVersion: this.cache.version,
              newVersion: newCache.version,
            });
            this.cache = newCache;
          }
        } catch (error) {
          console.warn('Failed to sync cache from other tab:', error);
        }
      }
    });
  }

  /**
   * Load cache from localStorage
   */
  private loadFromStorage(): LRUCache {
    try {
      const stored = localStorage.getItem(CACHE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored) as LRUCache;
        // Handle migration from old format
        if ((parsed as any).items) {
          // Migrate old format to new timeline format
          const timelines: Record<string, InvitationTimeline> = {};
          Object.entries((parsed as any).items).forEach(([id, oldItem]: [string, any]) => {
            timelines[id] = {
              invitationId: id,
              snapshots: [
                {
                  invitation: oldItem.invitation,
                  reason: oldItem.reason,
                  timestamp: oldItem.timestamp,
                  sequenceId: 1,
                },
              ],
              lastUpdated: oldItem.timestamp,
              firstSeen: oldItem.timestamp,
            };
          });

          return {
            timelines,
            order: parsed.order || [],
            lastModified: parsed.lastModified || Date.now(),
            version: parsed.version || 1,
          };
        }

        return {
          timelines: parsed.timelines || {},
          order: parsed.order || [],
          lastModified: parsed.lastModified || Date.now(),
          version: parsed.version || 1,
        };
      }
    } catch (error) {
      console.warn('Failed to load invitation diagnostics cache from localStorage:', error);
    }

    return {
      timelines: {},
      order: [],
      lastModified: Date.now(),
      version: 1,
    };
  }

  /**
   * Save cache to localStorage with conflict detection
   */
  private saveToStorage(): void {
    try {
      // Check for conflicts before saving
      const currentStored = localStorage.getItem(CACHE_KEY);
      if (currentStored) {
        const storedCache = JSON.parse(currentStored) as LRUCache;
        if (storedCache.version > this.cache.version) {
          console.warn('Cache conflict detected, reloading from storage...');
          this.cache = storedCache;
          return;
        }
      }

      // Update metadata and save
      this.cache.lastModified = Date.now();
      this.cache.version = (this.cache.version || 0) + 1;

      localStorage.setItem(CACHE_KEY, JSON.stringify(this.cache));
    } catch (error) {
      console.warn('Failed to save invitation diagnostics cache to localStorage:', error);
    }
  }

  /**
   * Enable or disable the cache globally
   */
  public setEnabled(enabled: boolean): void {
    cacheEnabled = enabled;
    localStorage.setItem(CACHE_ENABLED_KEY, enabled.toString());
    console.log(`Invitation diagnostics cache ${enabled ? 'enabled' : 'disabled'}`);
  }

  /**
   * Check if cache is currently enabled
   */
  public isEnabled(): boolean {
    return cacheEnabled;
  }

  /**
   * Add or update an invitation in the cache (creates timeline snapshots)
   */
  public set(invitationId: string, invitation: Invitation, reason: InvitationSnapshot['reason']): void {
    if (!cacheEnabled) return; // Noop when disabled
    const now = Date.now();

    // Determine the actual reason based on whether we've seen this invitation before
    let actualReason = reason;

    if (reason === 'updated') {
      if (!this.seenInvitationIds.has(invitationId)) {
        // First time seeing this invitation, mark as "added"
        actualReason = 'added';
        this.seenInvitationIds.add(invitationId);
      } else if (invitation.invalid) {
        // Invitation became invalid - this is effectively a removal
        actualReason = 'removed';
      } else {
        // We've seen this invitation before and it's still valid, keep as "updated"
        actualReason = 'updated';
      }
    } else if (reason === 'removed') {
      // Explicitly removed invitations stay as "removed"
      actualReason = 'removed';
    }

    // Remove from current position if exists
    this.cache.order = this.cache.order.filter((id) => id !== invitationId);
    // Add to front (most recently used)
    this.cache.order.unshift(invitationId);

    // Get or create timeline
    let timeline = this.cache.timelines[invitationId];
    if (!timeline) {
      timeline = {
        invitationId,
        snapshots: [],
        lastUpdated: now,
        firstSeen: now,
      };
    }

    // Create new snapshot
    const newSnapshot: InvitationSnapshot = {
      invitation: { ...invitation }, // Deep copy to avoid reference issues
      reason: actualReason,
      timestamp: now,
      sequenceId: timeline.snapshots.length + 1,
    };

    // Add snapshot to timeline
    timeline.snapshots.push(newSnapshot);
    timeline.lastUpdated = now;

    // Update the timeline in cache
    this.cache.timelines[invitationId] = timeline;

    // Enforce max size by removing least recently used timelines
    while (this.cache.order.length > MAX_CACHE_SIZE) {
      const lruId = this.cache.order.pop();
      if (lruId && this.cache.timelines[lruId]) {
        delete this.cache.timelines[lruId];
        // Also remove from seen set when evicted from cache
        this.seenInvitationIds.delete(lruId);
      }
    }

    this.saveToStorage();
  }

  /**
   * Get the latest snapshot for an invitation (backwards compatibility)
   */
  public get(invitationId: string): CachedInvitation | null {
    if (!cacheEnabled) return null; // Noop when disabled
    const timeline = this.cache.timelines[invitationId];
    if (!timeline || timeline.snapshots.length === 0) {
      return null;
    }

    // Move to front (mark as recently accessed)
    this.cache.order = this.cache.order.filter((id) => id !== invitationId);
    this.cache.order.unshift(invitationId);

    this.saveToStorage();

    // Return latest snapshot in old format
    const latestSnapshot = timeline.snapshots[timeline.snapshots.length - 1];
    return {
      invitation: latestSnapshot.invitation,
      lastSeen: timeline.lastUpdated,
      reason: latestSnapshot.reason,
      timestamp: latestSnapshot.timestamp,
    };
  }

  /**
   * Get the complete timeline for an invitation
   */
  public getTimeline(invitationId: string): InvitationTimeline | null {
    if (!cacheEnabled) return null;
    const timeline = this.cache.timelines[invitationId];
    if (!timeline) return null;

    // Move to front (mark as recently accessed)
    this.cache.order = this.cache.order.filter((id) => id !== invitationId);
    this.cache.order.unshift(invitationId);

    this.saveToStorage();
    return timeline;
  }

  /**
   * Get all timelines
   */
  public getAllTimelines(): InvitationTimeline[] {
    if (!cacheEnabled) return [];
    return this.cache.order.map((id) => this.cache.timelines[id]).filter(Boolean);
  }

  /**
   * Get all cached invitations (most recent first) - backwards compatibility
   */
  public getAll(): CachedInvitation[] {
    if (!cacheEnabled) return []; // Noop when disabled
    return this.cache.order
      .map((id) => this.cache.timelines[id])
      .filter(Boolean)
      .map((timeline) => {
        const latestSnapshot = timeline.snapshots[timeline.snapshots.length - 1];
        return {
          invitation: latestSnapshot.invitation,
          lastSeen: timeline.lastUpdated,
          reason: latestSnapshot.reason,
          timestamp: latestSnapshot.timestamp,
        };
      });
  }

  /**
   * Check if an invitation exists in cache
   */
  public has(invitationId: string): boolean {
    if (!cacheEnabled) return false; // Noop when disabled
    return invitationId in this.cache.timelines;
  }

  /**
   * Remove an invitation timeline from cache
   */
  public delete(invitationId: string): boolean {
    if (!cacheEnabled) return false; // Noop when disabled
    if (!this.cache.timelines[invitationId]) {
      return false;
    }

    delete this.cache.timelines[invitationId];
    this.cache.order = this.cache.order.filter((id) => id !== invitationId);
    this.seenInvitationIds.delete(invitationId);

    this.saveToStorage();
    return true;
  }

  /**
   * Clear all cached invitations (always works regardless of enabled state)
   */
  public clear(): void {
    this.cache = {
      timelines: {},
      order: [],
      lastModified: Date.now(),
      version: 1,
    };
    this.seenInvitationIds.clear(); // Also clear the seen IDs set

    // Always save to localStorage even if cache is disabled
    // This allows clearing old data when cache is disabled
    try {
      localStorage.setItem(CACHE_KEY, JSON.stringify(this.cache));
      console.log('Invitation diagnostics cache cleared from localStorage');
    } catch (error) {
      console.warn('Failed to clear invitation diagnostics cache from localStorage:', error);
    }
  }

  /**
   * Get cache statistics
   */
  public getStats() {
    if (!cacheEnabled) {
      return {
        totalItems: 0,
        maxSize: MAX_CACHE_SIZE,
        reasonCounts: {},
        oldestEntry: null,
        newestEntry: null,
        enabled: false,
      };
    }
    const items = this.getAll();
    const reasonCounts = items.reduce(
      (acc, item) => {
        acc[item.reason] = (acc[item.reason] || 0) + 1;
        return acc;
      },
      {} as Record<string, number>,
    );

    return {
      totalItems: items.length,
      maxSize: MAX_CACHE_SIZE,
      reasonCounts,
      oldestEntry: items[items.length - 1]?.timestamp || null,
      newestEntry: items[0]?.timestamp || null,
      enabled: true,
    };
  }
}

// Export singleton instance
export const invitationDiagnosticsCache = new InvitationDiagnosticsCache();

// Export types for use in components
export type { InvitationSnapshot, InvitationTimeline, CachedInvitation };
